"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ImplementationRulesReviewIssueCode = void 0;
exports.validateImplementationRulesReviewOutput = validateImplementationRulesReviewOutput;
exports.finalRuleSections = finalRuleSections;
const ImplementationRules = require("../../../../ImplementationRules");
const implementation_rules_review_types_1 = require("./implementation-rules-review.types");
var ImplementationRulesReviewIssueCode;
(function (ImplementationRulesReviewIssueCode) {
    ImplementationRulesReviewIssueCode["MissingExcerptVersion"] = "missing_excerpt_version";
    ImplementationRulesReviewIssueCode["NoGroups"] = "no_groups";
    ImplementationRulesReviewIssueCode["GroupNotReviewed"] = "group_not_reviewed";
    ImplementationRulesReviewIssueCode["MissingVerdict"] = "missing_verdict";
    ImplementationRulesReviewIssueCode["MissingRule"] = "missing_rule";
    ImplementationRulesReviewIssueCode["MissingRejectNote"] = "missing_reject_note";
    ImplementationRulesReviewIssueCode["InvalidRule"] = "invalid_rule";
    ImplementationRulesReviewIssueCode["DuplicateRuleId"] = "duplicate_rule_id";
    ImplementationRulesReviewIssueCode["DuplicateSectionId"] = "duplicate_section_id";
    ImplementationRulesReviewIssueCode["InvalidSectionSlug"] = "invalid_section_slug";
    ImplementationRulesReviewIssueCode["DuplicateSectionSlug"] = "duplicate_section_slug";
})(ImplementationRulesReviewIssueCode || (exports.ImplementationRulesReviewIssueCode = ImplementationRulesReviewIssueCode = {}));
const SECTION_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function validateImplementationRulesReviewOutput(input, output) {
    return [...progressIssues(output), ...verdictIssues(input, output), ...ruleSetIssues(output, input)];
}
function finalRuleSections(input, output) {
    const issues = validateImplementationRulesReviewOutput(input, output);
    if (issues.length > 0) {
        throw new Error(`Implementation-rules review cannot be finalized: ${issues.map((issue) => issue.detail).join('; ')}`);
    }
    return ImplementationRules.ImplementationRuleSectionsSchema.parse(nonEmptyKeptDrafts(input, output));
}
function progressIssues(output) {
    const issues = [];
    if (output.excerptVersion === undefined || output.excerptVersion.trim() === '') {
        issues.push({
            code: ImplementationRulesReviewIssueCode.MissingExcerptVersion,
            detail: 'The review does not record the excerpt version it was made against.',
        });
    }
    const groups = Object.entries(output.groups ?? {});
    if (groups.length === 0) {
        issues.push({
            code: ImplementationRulesReviewIssueCode.NoGroups,
            detail: 'The review records no excerpt groups.',
        });
    }
    for (const [groupId, group] of groups) {
        if (!group.reviewed) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.GroupNotReviewed,
                detail: `Excerpt group '${groupId}' is not marked reviewed.`,
            });
        }
    }
    return issues;
}
function verdictIssues(input, output) {
    const issues = [];
    const editedIds = new Set(editedSections(input, output).flatMap((section) => section.rules.map((rule) => rule.id)));
    for (const rule of input.implementationRules.flatMap((section) => section.rules)) {
        const verdict = output.cards?.[rule.id]?.verdict;
        if (verdict === undefined) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.MissingVerdict,
                detail: `Rule '${rule.id}' has no verdict.`,
            });
        }
        else if (verdict !== implementation_rules_review_types_1.CardVerdict.Rejected && !editedIds.has(rule.id)) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.MissingRule,
                detail: `Rule '${rule.id}' is missing from the edited rules but was not rejected.`,
            });
        }
    }
    for (const ruleId of editedIds) {
        const card = output.cards?.[ruleId];
        if (card?.verdict === implementation_rules_review_types_1.CardVerdict.Rejected && (card.note ?? '').trim() === '') {
            issues.push({
                code: ImplementationRulesReviewIssueCode.MissingRejectNote,
                detail: `Rejected rule '${ruleId}' has no note saying why.`,
            });
        }
    }
    return issues;
}
function ruleSetIssues(output, input) {
    const issues = [];
    const seenRuleIds = new Set();
    for (const rule of editedSections(input, output).flatMap((section) => section.rules)) {
        if (seenRuleIds.has(rule.id)) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.DuplicateRuleId,
                detail: `More than one rule has id '${rule.id}'.`,
            });
        }
        seenRuleIds.add(rule.id);
    }
    const seenSectionIds = new Set();
    const seenSlugs = new Set();
    for (const section of nonEmptyKeptDrafts(input, output)) {
        for (const rule of section.rules) {
            const parsed = ImplementationRules.ImplementationRuleSchema.safeParse(rule);
            if (!parsed.success) {
                issues.push({
                    code: ImplementationRulesReviewIssueCode.InvalidRule,
                    detail: `Rule '${rule.id}' is not a valid rule: ${parsed.error.issues
                        .map((zodIssue) => zodIssue.message)
                        .join('; ')}`,
                });
            }
        }
        if (seenSectionIds.has(section.sectionId)) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.DuplicateSectionId,
                detail: `More than one section has id '${section.sectionId}'.`,
            });
        }
        seenSectionIds.add(section.sectionId);
        if (!SECTION_SLUG_PATTERN.test(section.sectionSlug)) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.InvalidSectionSlug,
                detail: `Section slug '${section.sectionSlug}' is not lower-case kebab case.`,
            });
        }
        else if (seenSlugs.has(section.sectionSlug)) {
            issues.push({
                code: ImplementationRulesReviewIssueCode.DuplicateSectionSlug,
                detail: `More than one section has slug '${section.sectionSlug}'.`,
            });
        }
        seenSlugs.add(section.sectionSlug);
    }
    return issues;
}
function editedSections(input, output) {
    return output.rules ?? input.implementationRules;
}
function nonEmptyKeptDrafts(input, output) {
    const isKept = (rule) => output.cards?.[rule.id]?.verdict !== implementation_rules_review_types_1.CardVerdict.Rejected;
    return editedSections(input, output)
        .map((section) => ({ ...section, rules: section.rules.filter(isKept) }))
        .filter((section) => section.rules.length > 0);
}
//# sourceMappingURL=implementation-rules-review-validate.js.map